import React, { useState, useEffect, useRef } from "react";
import {
  GlobalStyle,
  OlympiacosTheme,
  colors,
  breakpoints,
} from "../../components/common/mixins";
import olympiacosHero from "../../assets/images/olympiacos-hero.jpg";
import olympiacosHeroMobile from "../../assets/images/olympiacos-hero-mobile.jpg";
import { InfoIcon } from "../../components/icons/InfoIcon";
import { Warning } from "../../components/icons/Warning";
import { Container } from "../../components/common/Container";
import { CenteredOverlay } from "../../components/common/CenteredOverlay";
import { EventCard, EventProps } from "../../components/common/EventCard";
import { InfoModal } from "../reservationPage/InfoModal";
import { OlympiacosPageSkeleton } from "./olympiacos-page-skeleton";
import { apiGet } from "../../api/fetch";
import { endpoints } from "../../utils/Constants";
import { EventCategory } from "../../common/enums/EventCategory";
import {
  getCountry,
  getLang,
  sitePolicyState,
  sanitizeWithNewTabLinks,
} from "../../utils/Utils";
import useModal from "../../hooks/useModal";
import SitePolicyPrompt from "../../components/SitePolicyPrompt.jsx";
import {
  OlympiacosScopedGlobalStyle,
  ContentSection,
  HeroBanner,
  HeroBannerMedia,
  HeroBannerImage,
  HeroBannerGradient,
  HeroBannerTextOverlay,
  BannerText,
  Title,
  SubTitle,
  Section,
  EventsGrid,
  EventsContainer,
  EventsTitle,
  InfoBanner,
  InfoWrapper,
  InfoHeader,
  TextWrapper,
  Text,
  TextBold,
  BulletText,
  Link,
} from "./olympiacos-page.styles";

const OLYMPIACOS_PRODUCER_ID = 2538;

type OlympiacosResources = {
  Title: string;
  SubTitle: string;
  AvailableEvents: string;
  SportsSeason: string;
  And: string;
  Here: string;
  Information: string;
  InformationText1: string;
  InformationText2: string;
  InformationText3: string;
  InformationText4: string;
  InformationText5: string;
  InformationText6: string;
  InformationText7: string;
  InformationText8: string;
  InformationText9: string;
  InformationText10: string;
  InformationText11: string;
  InformationText12: string;
  InformationText13: string;
  InformationText14: string;
  SomethingWrong: string;
  SomethingWrongText: string;
  TryAgain: string;
  SoldOut: string;
  TermsTitle: string;
  TicketingTermsLink: string;
  TransferTermsLink: string;
  StadiumRegulationsLink: string;
  PrivacyPolicyLink: string;
  TermsOfUseLink: string;
};

export function OlympiacosApp() {
  const [events, setEvents] = useState<EventProps[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const {
    isOpen: isErrorOpen,
    openModal: openError,
    closeModal: closeError,
  } = useModal(false, { disableScrollLock: true });
  const sitePolicyPromptRef = useRef<{
    open: (reason?: string, url?: string) => void;
  }>(null);

  const resources = window.homepage_resources as unknown as OlympiacosResources;

  const handleCardClick =
    (event: EventProps) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (event.categoryId === EventCategory.Sports && sitePolicyState()) {
        e.preventDefault();
        sitePolicyPromptRef.current?.open(undefined, event.redirectUrl);
      }
    };

  useEffect(() => {
    const country = getCountry();
    const lang = getLang();
    apiGet(
      endpoints.events.getEventsByProducer(
        country,
        lang,
        OLYMPIACOS_PRODUCER_ID,
        EventCategory.Sports,
      ),
    )
      .then(({ data }) => {
        const localePrefix = `/${country}-${lang}`;
        const sorted = (data ?? [])
          .sort(
            (a: EventProps, b: EventProps) =>
              Number(b.isSeasonTicket) - Number(a.isSeasonTicket),
          )
          .map((event: EventProps) => ({
            ...event,
            redirectUrl:
              event.redirectUrl &&
              !/^\/[a-z]{2}-[a-z]{2}\//.test(event.redirectUrl)
                ? `${localePrefix}/tickets${event.redirectUrl}`
                : event.redirectUrl,
          }));
        setEvents(sorted);
      })
      .catch((error) => {
        console.error("Failed to fetch Olympiacos events", error);
        openError();
      })
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) return <OlympiacosPageSkeleton />;

  return (
    <>
      <OlympiacosTheme />
      <GlobalStyle />
      <OlympiacosScopedGlobalStyle />

      <ContentSection>
        <div>
          {/* Banner Section */}
          <HeroBanner>
            <HeroBannerMedia>
              <picture>
                <source
                  media={`(min-width: ${breakpoints.mobile}px)`}
                  srcSet={olympiacosHero}
                />
                <HeroBannerImage src={olympiacosHeroMobile} alt="banner" />
              </picture>
              <HeroBannerGradient />
            </HeroBannerMedia>
            <HeroBannerTextOverlay>
              <Container>
                <BannerText>
                  <Title>{resources.Title}</Title>
                  <SubTitle>{resources.SubTitle}</SubTitle>
                </BannerText>
              </Container>
            </HeroBannerTextOverlay>
          </HeroBanner>

          {/* Events Section */}
          <Container>
            <Section>
              <EventsContainer>
                <EventsTitle>{resources.AvailableEvents}</EventsTitle>
                {events.length > 0 && (
                  <EventsGrid>
                    {events.map((event) => (
                      <EventCard
                        key={event.eventGroupId}
                        {...event}
                        dateLabel={
                          event.isSeasonTicket
                            ? resources.SportsSeason
                            : undefined
                        }
                        labels={{ soldOut: resources.SoldOut }}
                        onClick={handleCardClick(event)}
                      />
                    ))}
                  </EventsGrid>
                )}
              </EventsContainer>

              {/* Info Section */}
              <InfoBanner>
                <InfoWrapper>
                  <div>
                    <InfoIcon color="none" strokeColor={colors.darkText} />
                  </div>

                  <TextWrapper>
                    <InfoHeader>{resources.Information}</InfoHeader>
                    <div>
                      <Text>
                        {resources.InformationText1}{" "}
                        <Link
                          href="https://www.olympiacos.org"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          www.olympiacos.org
                        </Link>{" "}
                        {resources.And}{" "}
                        <Link
                          href="https://www.more.com"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          more.com
                        </Link>
                        .
                      </Text>
                      <TextBold>{resources.InformationText2}</TextBold>
                    </div>

                    <div>
                      <TextBold>{resources.InformationText3}</TextBold>
                      <Text>{resources.InformationText10}{" "}{resources.InformationText11}</Text>
                      <Text>
                        {resources.InformationText5}{" "}
                        <Link
                          href="https://membersolympiacos.gr/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {resources.Here}
                        </Link>
                        .
                      </Text>
                      <Text>
                        {resources.InformationText7}{" "}
                        <Link
                          href="https://www.olympiacossfp.gr"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          https://www.olympiacossfp.gr
                        </Link>
                      </Text>
                      <Text>
                        {resources.InformationText6}{" "}
                        <Link href="mailto:members@osfp.gr">
                          members@osfp.gr
                        </Link>{" "}
                        {resources.InformationText8}
                      </Text>
                      <Text>{resources.InformationText9}</Text>
                    </div>
                    
                    <div>
                      <TextBold>{resources.InformationText12}</TextBold>
                      <Text>{resources.InformationText13}{" "}
                        <Link
                            href="https://tickets.gov.gr/wp-content/uploads/2024/10/metavivasi_instructions_compressed.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                          {resources.Here}
                        </Link>
                        .
                      </Text>
                      <TextBold>{resources.InformationText14}</TextBold>
                    </div>
                    
                    <div>
                      <TextBold>{resources.TermsTitle}</TextBold>
                      <Text
                        dangerouslySetInnerHTML={{
                          __html: sanitizeWithNewTabLinks(
                            resources.TicketingTermsLink,
                          ),
                        }}
                      />
                      <Text
                        dangerouslySetInnerHTML={{
                          __html: sanitizeWithNewTabLinks(
                            resources.TransferTermsLink,
                          ),
                        }}
                      />
                      <Text
                        dangerouslySetInnerHTML={{
                          __html: sanitizeWithNewTabLinks(
                            resources.StadiumRegulationsLink,
                          ),
                        }}
                      />
                      <Text
                        dangerouslySetInnerHTML={{
                          __html: sanitizeWithNewTabLinks(
                            resources.PrivacyPolicyLink,
                          ),
                        }}
                      />
                      <Text
                        dangerouslySetInnerHTML={{
                          __html: sanitizeWithNewTabLinks(
                            resources.TermsOfUseLink,
                          ),
                        }}
                      />
                    </div>
                  </TextWrapper>
                </InfoWrapper>
              </InfoBanner>
            </Section>
          </Container>
        </div>
      </ContentSection>

      <SitePolicyPrompt
        intent="view"
        mandatory={false}
        autoOpen={false}
        ref={sitePolicyPromptRef}
      />

      <CenteredOverlay
        open={isErrorOpen}
        handleClose={closeError}
        id="olympiacos-error"
        titleText={resources.SomethingWrong}
        icon={<Warning color="#FC2D33" />}
        size="sm"
      >
        <InfoModal
          description={resources.SomethingWrongText}
          buttonText={resources.TryAgain}
          onClick={closeError}
        />
      </CenteredOverlay>
    </>
  );
}
